// Master Data Management
// All configurable values for the platform

export interface MasterDataItem {
  id: string;
  name: string;
  code?: string;
  description?: string;
  isActive: boolean;
  order?: number;
  metadata?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
}

export interface MasterDataCategory {
  key: string;
  label: string;
  description: string;
  icon: string;
  fields: {
    name: string;
    type: 'text' | 'textarea' | 'color' | 'number' | 'select';
    label: string;
    required?: boolean;
    options?: string[];
  }[];
}

// Master Data Categories
export const MASTER_DATA_CATEGORIES: MasterDataCategory[] = [
  {
    key: 'userRoles',
    label: 'User Roles',
    description: 'System user roles and permissions',
    icon: 'Shield',
    fields: [
      { name: 'name', type: 'text', label: 'Role Name', required: true },
      { name: 'code', type: 'text', label: 'Role Code', required: true },
      { name: 'description', type: 'textarea', label: 'Description' }
    ]
  },
  {
    key: 'cuisineTypes',
    label: 'Cuisine Types',
    description: 'Available cuisine categories',
    icon: 'ChefHat',
    fields: [
      { name: 'name', type: 'text', label: 'Cuisine Name', required: true },
      { name: 'description', type: 'textarea', label: 'Description' }
    ]
  },
  {
    key: 'mealTypes',
    label: 'Meal Types',
    description: 'Breakfast, Lunch, Dinner, etc.',
    icon: 'Clock',
    fields: [
      { name: 'name', type: 'text', label: 'Meal Type', required: true },
      { name: 'description', type: 'textarea', label: 'Description' }
    ]
  },
  {
    key: 'foodCategories',
    label: 'Food Categories',
    description: 'Global categories for menu classification',
    icon: 'Tag',
    fields: [
      { name: 'name', type: 'text', label: 'Category Name', required: true },
      { name: 'description', type: 'textarea', label: 'Description' }
    ]
  },
  {
    key: 'dietaryTags',
    label: 'Dietary Tags',
    description: 'Vegan, Vegetarian, Gluten-Free, etc.',
    icon: 'Award',
    fields: [
      { name: 'name', type: 'text', label: 'Tag Name', required: true },
      { name: 'color', type: 'color', label: 'Color' },
      { name: 'description', type: 'textarea', label: 'Description' }
    ]
  },
  {
    key: 'spiceLevels',
    label: 'Spice Levels',
    description: 'Mild, Medium, Hot, Extra Hot',
    icon: 'Flame',
    fields: [
      { name: 'name', type: 'text', label: 'Level Name', required: true },
      { name: 'level', type: 'number', label: 'Level (1-5)', required: true },
      { name: 'color', type: 'color', label: 'Color' }
    ]
  },
  {
    key: 'orderStatuses',
    label: 'Order Statuses',
    description: 'Pending, Confirmed, Preparing, etc.',
    icon: 'Package',
    fields: [
      { name: 'name', type: 'text', label: 'Status Name', required: true },
      { name: 'code', type: 'text', label: 'Status Code', required: true },
      { name: 'color', type: 'color', label: 'Color' },
      { name: 'description', type: 'textarea', label: 'Description' }
    ]
  },
  {
    key: 'paymentMethods',
    label: 'Payment Methods',
    description: 'Cash, Card, UPI, Wallet, etc.',
    icon: 'CreditCard',
    fields: [
      { name: 'name', type: 'text', label: 'Method Name', required: true },
      { name: 'code', type: 'text', label: 'Method Code', required: true },
      { name: 'description', type: 'textarea', label: 'Description' }
    ]
  },
  {
    key: 'paymentStatuses',
    label: 'Payment Statuses',
    description: 'Pending, Paid, Failed, Refunded',
    icon: 'DollarSign',
    fields: [
      { name: 'name', type: 'text', label: 'Status Name', required: true },
      { name: 'code', type: 'text', label: 'Status Code', required: true },
      { name: 'color', type: 'color', label: 'Color' }
    ]
  },
  {
    key: 'deliveryStatuses',
    label: 'Delivery Statuses',
    description: 'Assigned, Picked Up, On the Way, Delivered',
    icon: 'Truck',
    fields: [
      { name: 'name', type: 'text', label: 'Status Name', required: true },
      { name: 'code', type: 'text', label: 'Status Code', required: true },
      { name: 'color', type: 'color', label: 'Color' }
    ]
  },
  {
    key: 'amenities',
    label: 'Amenities & Facilities',
    description: 'WiFi, Parking, AC, etc.',
    icon: 'Home',
    fields: [
      { name: 'name', type: 'text', label: 'Amenity Name', required: true },
      { name: 'description', type: 'textarea', label: 'Description' }
    ]
  },
  {
    key: 'tableStatuses',
    label: 'Table Statuses',
    description: 'Available, Occupied, Reserved, Cleaning',
    icon: 'Grid',
    fields: [
      { name: 'name', type: 'text', label: 'Status Name', required: true },
      { name: 'code', type: 'text', label: 'Status Code', required: true },
      { name: 'color', type: 'color', label: 'Color' }
    ]
  },
  {
    key: 'reservationStatuses',
    label: 'Reservation Statuses',
    description: 'Pending, Confirmed, Cancelled, Completed',
    icon: 'Calendar',
    fields: [
      { name: 'name', type: 'text', label: 'Status Name', required: true },
      { name: 'code', type: 'text', label: 'Status Code', required: true },
      { name: 'color', type: 'color', label: 'Color' }
    ]
  },
  {
    key: 'discountTypes',
    label: 'Discount Types',
    description: 'Percentage, Fixed Amount, Buy One Get One',
    icon: 'Percent',
    fields: [
      { name: 'name', type: 'text', label: 'Type Name', required: true },
      { name: 'code', type: 'text', label: 'Type Code', required: true },
      { name: 'description', type: 'textarea', label: 'Description' }
    ]
  },
  {
    key: 'offerCategories',
    label: 'Offer Categories',
    description: 'Festival Offers, Weekend Deals, etc.',
    icon: 'Tag',
    fields: [
      { name: 'name', type: 'text', label: 'Category Name', required: true },
      { name: 'description', type: 'textarea', label: 'Description' }
    ]
  },
  {
    key: 'reviewRatings',
    label: 'Review Ratings',
    description: 'Rating criteria (Food, Service, Ambience)',
    icon: 'Star',
    fields: [
      { name: 'name', type: 'text', label: 'Criteria Name', required: true },
      { name: 'description', type: 'textarea', label: 'Description' }
    ]
  },
  {
    key: 'complaintCategories',
    label: 'Complaint Categories',
    description: 'Food Quality, Delivery, Service, etc.',
    icon: 'AlertCircle',
    fields: [
      { name: 'name', type: 'text', label: 'Category Name', required: true },
      { name: 'description', type: 'textarea', label: 'Description' }
    ]
  },
  {
    key: 'departments',
    label: 'Departments',
    description: 'Staff departments for restaurants',
    icon: 'Briefcase',
    fields: [
      { name: 'name', type: 'text', label: 'Department Name', required: true },
      { name: 'code', type: 'text', label: 'Department Code', required: true },
      { name: 'description', type: 'textarea', label: 'Description' }
    ]
  },
  {
    key: 'countries',
    label: 'Countries',
    description: 'Supported countries',
    icon: 'Globe',
    fields: [
      { name: 'name', type: 'text', label: 'Country Name', required: true },
      { name: 'code', type: 'text', label: 'Country Code', required: true }
    ]
  },
  {
    key: 'states',
    label: 'States',
    description: 'States/Provinces by country',
    icon: 'Map',
    fields: [
      { name: 'name', type: 'text', label: 'State Name', required: true },
      { name: 'code', type: 'text', label: 'State Code', required: true },
      { name: 'countryId', type: 'select', label: 'Country', required: true }
    ]
  },
  {
    key: 'cities',
    label: 'Cities',
    description: 'Serviceable cities',
    icon: 'MapPin',
    fields: [
      { name: 'name', type: 'text', label: 'City Name', required: true },
      { name: 'countryId', type: 'select', label: 'Country', required: true },
      { name: 'stateId', type: 'select', label: 'State', required: true }
    ]
  },
  {
    key: 'seatingPreferences',
    label: 'Seating Preferences',
    description: 'Indoor, Outdoor, Any and future seating choices',
    icon: 'Armchair',
    fields: [
      { name: 'name', type: 'text', label: 'Preference Name', required: true },
      { name: 'code', type: 'text', label: 'Preference Code', required: true }
    ]
  },
  {
    key: 'timezones',
    label: 'Timezones',
    description: 'Available timezone options',
    icon: 'Globe',
    fields: [
      { name: 'name', type: 'text', label: 'Timezone Name', required: true },
      { name: 'code', type: 'text', label: 'Timezone Code', required: true }
    ]
  },
  {
    key: 'currencies',
    label: 'Currencies',
    description: 'Supported currencies',
    icon: 'DollarSign',
    fields: [
      { name: 'name', type: 'text', label: 'Currency Name', required: true },
      { name: 'code', type: 'text', label: 'Currency Code', required: true },
      { name: 'symbol', type: 'text', label: 'Symbol', required: true }
    ]
  },
  {
    key: 'languages',
    label: 'Languages',
    description: 'Supported languages',
    icon: 'Languages',
    fields: [
      { name: 'name', type: 'text', label: 'Language Name', required: true },
      { name: 'code', type: 'text', label: 'Language Code', required: true }
    ]
  },
  {
    key: 'priceRanges',
    label: 'Price Ranges',
    description: 'Restaurant price categories',
    icon: 'TrendingUp',
    fields: [
      { name: 'name', type: 'text', label: 'Range Name', required: true },
      { name: 'symbol', type: 'text', label: 'Symbol', required: true },
      { name: 'description', type: 'textarea', label: 'Description' }
    ]
  }
];

// Initialize default master data
export const getDefaultMasterData = (): Record<string, MasterDataItem[]> => {
  return {
    userRoles: [
      { id: '1', name: 'Customer', code: 'customer', description: 'Can browse and order food', isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'Kitchen Staff', code: 'staff', description: 'Manage kitchen operations', isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'Manager', code: 'manager', description: 'Manage restaurant operations', isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'Restaurant Admin', code: 'restaurant_admin', description: 'Full restaurant control', isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '5', name: 'Super Admin', code: 'superadmin', description: 'Platform-wide access', isActive: true, order: 5, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    cuisineTypes: [
      { id: '1', name: 'North Indian', isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'South Indian', isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'Chinese', isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'Italian', isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '5', name: 'Mexican', isActive: true, order: 5, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '6', name: 'Thai', isActive: true, order: 6, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '7', name: 'Japanese', isActive: true, order: 7, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '8', name: 'Continental', isActive: true, order: 8, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '9', name: 'Fast Food', isActive: true, order: 9, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '10', name: 'Street Food', isActive: true, order: 10, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '11', name: 'Bakery', isActive: true, order: 11, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '12', name: 'Desserts', isActive: true, order: 12, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '13', name: 'Beverages', isActive: true, order: 13, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '14', name: 'Healthy', isActive: true, order: 14, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '15', name: 'Vegan', isActive: true, order: 15, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    mealTypes: [
      { id: '1', name: 'Breakfast', isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'Brunch', isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'Lunch', isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'Dinner', isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '5', name: 'Snacks', isActive: true, order: 5, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    foodCategories: [
      { id: '1', name: 'Starters', isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'Main Course', isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'Desserts', isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'Beverages', isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    dietaryTags: [
      { id: '1', name: 'Vegetarian', metadata: { color: '#10b981' }, isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'Vegan', metadata: { color: '#22c55e' }, isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'Gluten-Free', metadata: { color: '#f59e0b' }, isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'Dairy-Free', metadata: { color: '#3b82f6' }, isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '5', name: 'Keto', metadata: { color: '#8b5cf6' }, isActive: true, order: 5, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '6', name: 'Halal', metadata: { color: '#06b6d4' }, isActive: true, order: 6, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '7', name: 'Jain', metadata: { color: '#ec4899' }, isActive: true, order: 7, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    spiceLevels: [
      { id: '1', name: 'Mild', metadata: { level: 1, color: '#10b981' }, isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'Medium', metadata: { level: 2, color: '#f59e0b' }, isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'Hot', metadata: { level: 3, color: '#f97316' }, isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'Extra Hot', metadata: { level: 4, color: '#ef4444' }, isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '5', name: 'Extreme', metadata: { level: 5, color: '#dc2626' }, isActive: true, order: 5, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    orderStatuses: [
      { id: '1', name: 'Pending', code: 'pending', metadata: { color: '#f59e0b' }, isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'Confirmed', code: 'confirmed', metadata: { color: '#3b82f6' }, isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'Preparing', code: 'preparing', metadata: { color: '#8b5cf6' }, isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'Ready', code: 'ready', metadata: { color: '#06b6d4' }, isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '5', name: 'Out for Delivery', code: 'out_for_delivery', metadata: { color: '#10b981' }, isActive: true, order: 5, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '6', name: 'Delivered', code: 'delivered', metadata: { color: '#22c55e' }, isActive: true, order: 6, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '7', name: 'Cancelled', code: 'cancelled', metadata: { color: '#ef4444' }, isActive: true, order: 7, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    paymentMethods: [
      { id: '1', name: 'Cash on Delivery', code: 'cod', isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'Credit/Debit Card', code: 'card', isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'UPI', code: 'upi', isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'Digital Wallet', code: 'wallet', isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '5', name: 'Net Banking', code: 'netbanking', isActive: true, order: 5, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    paymentStatuses: [
      { id: '1', name: 'Pending', code: 'pending', metadata: { color: '#f59e0b' }, isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'Paid', code: 'paid', metadata: { color: '#10b981' }, isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'Failed', code: 'failed', metadata: { color: '#ef4444' }, isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'Refunded', code: 'refunded', metadata: { color: '#8b5cf6' }, isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    deliveryStatuses: [
      { id: '1', name: 'Assigned', code: 'assigned', metadata: { color: '#3b82f6' }, isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'Picked Up', code: 'picked_up', metadata: { color: '#8b5cf6' }, isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'On the Way', code: 'on_the_way', metadata: { color: '#f59e0b' }, isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'Delivered', code: 'delivered', metadata: { color: '#10b981' }, isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    amenities: [
      { id: '1', name: 'WiFi', isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'Parking', isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'Air Conditioning', isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'Outdoor Seating', isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '5', name: 'Home Delivery', isActive: true, order: 5, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '6', name: 'Takeaway', isActive: true, order: 6, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '7', name: 'Live Music', isActive: true, order: 7, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '8', name: 'Pet Friendly', isActive: true, order: 8, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    tableStatuses: [
      { id: '1', name: 'Available', code: 'available', metadata: { color: '#10b981' }, isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'Occupied', code: 'occupied', metadata: { color: '#ef4444' }, isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'Reserved', code: 'reserved', metadata: { color: '#f59e0b' }, isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'Cleaning', code: 'cleaning', metadata: { color: '#8b5cf6' }, isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    reservationStatuses: [
      { id: '1', name: 'Pending', code: 'pending', metadata: { color: '#f59e0b' }, isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'Confirmed', code: 'confirmed', metadata: { color: '#10b981' }, isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'Cancelled', code: 'cancelled', metadata: { color: '#ef4444' }, isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'Completed', code: 'completed', metadata: { color: '#8b5cf6' }, isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    discountTypes: [
      { id: '1', name: 'Percentage', code: 'percentage', description: 'Discount in percentage', isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'Fixed Amount', code: 'fixed', description: 'Fixed amount discount', isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'Buy One Get One', code: 'bogo', description: 'Buy one get one free', isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    offerCategories: [
      { id: '1', name: 'Festival Offers', isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'Weekend Deals', isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'First Order', isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'Loyalty Rewards', isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    reviewRatings: [
      { id: '1', name: 'Food Quality', isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'Service', isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'Ambience', isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'Value for Money', isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '5', name: 'Delivery Speed', isActive: true, order: 5, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    complaintCategories: [
      { id: '1', name: 'Food Quality', isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'Late Delivery', isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'Wrong Order', isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'Poor Service', isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '5', name: 'Hygiene Issues', isActive: true, order: 5, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    departments: [],
    seatingPreferences: [],
    countries: [],
    states: [],
    cities: [
      { id: '1', name: 'Mumbai', metadata: { state: 'Maharashtra', country: 'India' }, isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'Delhi', metadata: { state: 'Delhi', country: 'India' }, isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'Bangalore', metadata: { state: 'Karnataka', country: 'India' }, isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'Hyderabad', metadata: { state: 'Telangana', country: 'India' }, isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '5', name: 'Chennai', metadata: { state: 'Tamil Nadu', country: 'India' }, isActive: true, order: 5, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '6', name: 'Kolkata', metadata: { state: 'West Bengal', country: 'India' }, isActive: true, order: 6, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '7', name: 'Pune', metadata: { state: 'Maharashtra', country: 'India' }, isActive: true, order: 7, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    timezones: [
      { id: '1', name: 'Asia/Kolkata (IST)', code: 'Asia/Kolkata', isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'America/New_York (EST)', code: 'America/New_York', isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'Europe/London (GMT)', code: 'Europe/London', isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'Asia/Dubai (GST)', code: 'Asia/Dubai', isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '5', name: 'Asia/Singapore (SGT)', code: 'Asia/Singapore', isActive: true, order: 5, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    currencies: [
      { id: '1', name: 'Indian Rupee', code: 'INR', metadata: { symbol: '₹' }, isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'US Dollar', code: 'USD', metadata: { symbol: '$' }, isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'Euro', code: 'EUR', metadata: { symbol: '€' }, isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'British Pound', code: 'GBP', metadata: { symbol: '£' }, isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    languages: [
      { id: '1', name: 'English', code: 'en', isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'Hindi', code: 'hi', isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'Spanish', code: 'es', isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'French', code: 'fr', isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ],
    priceRanges: [
      { id: '1', name: 'Budget Friendly', metadata: { symbol: '₹' }, description: 'Under ₹300 for two', isActive: true, order: 1, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '2', name: 'Moderate', metadata: { symbol: '₹₹' }, description: '₹300-₹700 for two', isActive: true, order: 2, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '3', name: 'Expensive', metadata: { symbol: '₹₹₹' }, description: '₹700-₹1500 for two', isActive: true, order: 3, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() },
      { id: '4', name: 'Fine Dining', metadata: { symbol: '₹₹₹₹' }, description: 'Above ₹1500 for two', isActive: true, order: 4, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() }
    ]
  };
};

// Get master data from localStorage or use defaults
export const getMasterData = (category: string): MasterDataItem[] => {
  const stored = localStorage.getItem(`masterData_${category}`);
  if (stored) {
    return JSON.parse(stored);
  }
  const defaults = getDefaultMasterData();
  return defaults[category] || [];
};

// Save master data to localStorage
export const saveMasterData = (category: string, data: MasterDataItem[]): void => {
  localStorage.setItem(`masterData_${category}`, JSON.stringify(data));
};

// Initialize all master data
export const initializeMasterData = (): void => {
  const defaults = getDefaultMasterData();
  Object.keys(defaults).forEach(category => {
    if (!localStorage.getItem(`masterData_${category}`)) {
      saveMasterData(category, defaults[category]);
    }
  });
};
